// admin/src/pages/SettlementManagementPage.jsx - 통합 정산 관리 페이지
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { saveAs } from 'file-saver';
import { 
  Calculator, 
  CheckCircle, 
  Clock, 
  AlertTriangle, 
  RefreshCw, 
  Banknote, 
  BarChart3, 
  FileText, 
  Settings, 
  DollarSign, 
  TrendingUp, 
  AlertCircle,
  Search,
  Filter,
  Eye,
  Edit,
  Trash2,
  Plus,
  Calendar,
  User,
  CreditCard,
  Shield,
  Zap,
  Star,
  Heart,
  Share2,
  Download,
  MoreVertical,
  Info
} from 'lucide-react';
import { 
  Button, 
  Input, 
  Select, 
  Badge, 
  Tooltip, 
  Dropdown, 
  Menu, 
  Pagination,
  Modal,
  Typography,
  Space,
  Tag,
  Switch,
  Divider,
  Card,
  Tabs,
  DatePicker,
  Table,
  Progress,
  Statistic,
  Row,
  Col
} from 'antd';

const { RangePicker } = DatePicker;
import { providerAccountService } from '../services/providerAccountService';
import { providerAccountAPI } from '../api/providerAccount';
import { db, appId } from '../firebase/firebaseConfig.jsx';
import { collection, query, orderBy, where, getDocs, doc, updateDoc, addDoc, deleteDoc, serverTimestamp, getDocFromServer, getDoc, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/firebaseConfig.jsx';
import { useToastMessage } from '../contexts/ToastMessageContext.jsx';
import { getDateTimestamp } from '../utils/dateUtils.js';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

// Modern CSS styles
const modernStyles = `
  .modern-tabs .ant-tabs-tab {
    border-radius: 12px;
    margin-right: 8px;
    padding: 12px 20px;
    border: 1px solid #e5e7eb;
    background: white;
    color: #374151 !important;
    transition: all 0.3s ease;
  }
  
  .modern-tabs .ant-tabs-tab:hover {
    border-color: #10b981;
    color: #10b981 !important;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.15);
  }
  
  .modern-tabs .ant-tabs-tab-active {
    background: linear-gradient(135deg, #10b981, #059669);
    border-color: #10b981;
    color: white !important;
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
  }
  
  .modern-tabs .ant-tabs-tab-active .ant-badge {
    color: white !important;
  }
  
  .modern-tabs .ant-tabs-tab .ant-badge {
    color: #374151 !important;
  }
  
  .modern-tabs .ant-tabs-tab-active .ant-badge-count {
    background: rgba(255, 255, 255, 0.3) !important;
    color: white !important;
    border-color: white !important;
  }
  
  .modern-pagination .ant-pagination-item {
    border-radius: 8px;
    border: 1px solid #e5e7eb;
  }
  
  .modern-pagination .ant-pagination-item:hover {
    border-color: #10b981;
    color: #10b981;
  }
  
  .modern-pagination .ant-pagination-item-active {
    background: linear-gradient(135deg, #10b981, #059669);
    border-color: #10b981;
    color: white;
  }
  
  .modern-pagination .ant-pagination-prev,
  .modern-pagination .ant-pagination-next {
    border-radius: 8px;
    border: 1px solid #e5e7eb;
  }
  
  .modern-pagination .ant-pagination-prev:hover,
  .modern-pagination .ant-pagination-next:hover {
    border-color: #10b981;
    color: #10b981;
  }
`;

const SettlementManagementPage = () => {
  const { showToastMessage } = useToastMessage();
  const [settlements, setSettlements] = useState([]);
  const [filteredSettlements, setFilteredSettlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSettlement, setSelectedSettlement] = useState(null);
  const [showSettlementModal, setShowSettlementModal] = useState(false);
  const [activeTab, setActiveTab] = useState('provider-settlement');
  
  // 월별 정산 상세 모달
  const [selectedWeek, setSelectedWeek] = useState(null); // month group (legacy name)
  const [showWeekDetails, setShowWeekDetails] = useState(false);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalSettlements, setTotalSettlements] = useState(0);
  
  // Filter states
  const [filters, setFilters] = useState({
    status: 'all',
    dateRange: '7days',
    amountRange: 'all',
    searchTerm: ''
  });
  
  // 금융설정 상태
  const [financeConfig, setFinanceConfig] = useState(null);
  const [financeLoading, setFinanceLoading] = useState(false);

  // 송금업체 계좌 상태
  const [providerAccounts, setProviderAccounts] = useState([]);
  const [providerLoading, setProviderLoading] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [accountForm, setAccountForm] = useState({
    bankName: '',
    accountNumber: '',
    accountHolder: '',
    bankCode: '',
    branchCode: '',
    isActive: true,
    description: '',
    dailyLimit: 0,
    monthlyLimit: 0
  });

  // Statistics
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    settled: 0,
    failed: 0,
    processing: 0,
    totalAmount: 0,
    totalFees: 0,
    totalMMK: 0
  });

  // 일괄/월별 정산 처리 상태
  const [selectedSettlements, setSelectedSettlements] = useState([]);
  const [batchSettling, setBatchSettling] = useState(false);
  const [settlingMonthKey, setSettlingMonthKey] = useState(null);
  
  // 캐시 업데이트 로딩 상태
  const [updatingCache, setUpdatingCache] = useState(false);

  // 정산 전: 기간 필터 없이 미정산(PENDING) 전체 (완료된 송금만)
  const providerPendingData = useMemo(
    () =>
      settlements.filter((s) => {
        if (s.status !== 'COMPLETED') return false;
        const status = s.settlementStatus !== undefined ? s.settlementStatus : 'PENDING';
        return status === 'PENDING';
      }),
    [settlements]
  );

  // 정산 완료: SETTLED 전체 (월별 조회용)
  const providerSettledData = useMemo(
    () =>
      settlements.filter((s) => {
        if (s.status !== 'COMPLETED') return false;
        return s.settlementStatus === 'SETTLED';
      }),
    [settlements]
  );

  // Excel 등 하위 호환
  const providerFilteredData = useMemo(
    () => [...providerPendingData, ...providerSettledData],
    [providerPendingData, providerSettledData]
  );


  // 주간 키 생성 함수 (ISO 8601 주 번호) - useCallback으로 메모이제이션
  const getWeekKey = useCallback((date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 3 - (d.getDay() + 6) % 7);
    const week1 = new Date(d.getFullYear(), 0, 4);
    const weekNum = 1 + Math.round(((d - week1) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
    return `${d.getFullYear()}-W${String(weekNum).padStart(2, '0')}`;
  }, []);

  // 주간 시작일/종료일 계산 - useCallback으로 메모이제이션
  const getWeekRange = useCallback((date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // 월요일 기준
    const startDate = new Date(d.setDate(diff));
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6);
    endDate.setHours(23, 59, 59, 999);
    return { startDate, endDate };
  }, []);

  // 헥토파이낸스 정산: 건당 고정액 (기본 3,000원)
  const getHectoFeePerTx = useCallback((config) => {
    const v = Number(
      config?.settlementConfig?.hectoFeePerTransactionKRW ??
      config?.feeSettings?.hectoFeePerTransactionKRW ??
      3000
    );
    return Number.isFinite(v) && v >= 0 ? Math.round(v) : 3000;
  }, []);

  const calculateProviderSettlement = useCallback(
    (remittance, config) => getHectoFeePerTx(config),
    [getHectoFeePerTx]
  );

  // Kairos 정산: 사용자 수수료(송금액의 2.5%) − 헥토 건당 정산액
  const calculateKairosSettlement = useCallback((remittance, config) => {
    const gross =
      Number(remittance.grossPaymentKRW || remittance.totalDepositKRW || 0) ||
      (Number(remittance.amountKRW || 0) + Number(remittance.feeKRW || 0));
    const ratePct = Number(config?.feeSettings?.remittanceFeeRatePercent ?? 2.5);
    const fee = Number(remittance.feeKRW) > 0 ? Number(remittance.feeKRW) : Math.round(gross * (ratePct / 100));
    return Math.round(fee) - getHectoFeePerTx(config);
  }, [getHectoFeePerTx]);

  const getRemittanceGrossKRW = useCallback((remittance) => {
    return (
      Number(remittance.grossPaymentKRW || remittance.totalDepositKRW || 0) ||
      Number(remittance.amountKRW || 0) + Number(remittance.feeKRW || 0)
    );
  }, []);

  // 정산 전(PENDING) 요약 카드
  const providerPendingSummary = useMemo(() => {
    let totalAmount = 0;
    let kairosShare = 0;
    let hectoShare = 0;

    providerPendingData.forEach((s) => {
      totalAmount += getRemittanceGrossKRW(s);
      kairosShare += calculateKairosSettlement(s, financeConfig);
      hectoShare += calculateProviderSettlement(s, financeConfig);
    });

    return {
      count: providerPendingData.length,
      totalAmount,
      kairosShare,
      hectoShare,
    };
  }, [
    providerPendingData,
    financeConfig,
    calculateKairosSettlement,
    calculateProviderSettlement,
    getRemittanceGrossKRW,
  ]);

  const providerSettledSummary = useMemo(() => {
    let totalAmount = 0;
    let kairosShare = 0;
    let hectoShare = 0;

    providerSettledData.forEach((s) => {
      totalAmount += getRemittanceGrossKRW(s);
      kairosShare += calculateKairosSettlement(s, financeConfig);
      hectoShare += calculateProviderSettlement(s, financeConfig);
    });

    return {
      count: providerSettledData.length,
      totalAmount,
      kairosShare,
      hectoShare,
    };
  }, [
    providerSettledData,
    financeConfig,
    calculateKairosSettlement,
    calculateProviderSettlement,
    getRemittanceGrossKRW,
  ]);

  // 송금업체 정산 통계 (하위 호환)
  const providerStats = useMemo(
    () => ({
      total: providerFilteredData.length,
      settled: providerSettledData.length,
      pending: providerPendingData.length,
      settledAmount: providerSettledSummary.hectoShare,
    }),
    [
      providerFilteredData.length,
      providerSettledData.length,
      providerPendingData.length,
      providerSettledSummary.hectoShare,
    ]
  );

  const getCompletedDate = useCallback((remittance) => {
    const raw =
      remittance.uabCompletedAt ||
      remittance.completedAt ||
      remittance.createdAt;
    if (!raw) return null;
    return raw?.toDate ? raw.toDate() : new Date(raw);
  }, []);

  // 월 단위 그룹화 (정산 처리 단위)
  const groupByMonth = useCallback((remittances, config) => {
    const monthlyGroups = {};

    remittances.forEach((remittance) => {
      const completedDate = getCompletedDate(remittance);
      if (!completedDate || Number.isNaN(completedDate.getTime())) return;

      const monthKey = dayjs(completedDate).format('YYYY-MM');
      const startDate = dayjs(completedDate).startOf('month').toDate();
      const endDate = dayjs(completedDate).endOf('month').toDate();

      if (!monthlyGroups[monthKey]) {
        monthlyGroups[monthKey] = {
          weekKey: monthKey, // 모달 호환
          monthKey,
          startDate,
          endDate,
          remittances: [],
          providerSettlement: 0,
          kairosSettlement: 0,
          totalAmount: 0,
          totalFees: 0,
          totalUSD: 0,
          totalMMK: 0,
          transactionCount: 0,
        };
      }

      monthlyGroups[monthKey].remittances.push(remittance);
      monthlyGroups[monthKey].providerSettlement += calculateProviderSettlement(remittance, config);
      monthlyGroups[monthKey].kairosSettlement += calculateKairosSettlement(remittance, config);
      monthlyGroups[monthKey].totalAmount += remittance.amountKRW || 0;
      monthlyGroups[monthKey].totalFees += remittance.feeKRW || 0;
      monthlyGroups[monthKey].totalUSD += remittance.amountUSD || remittance.receiver?.amountUSD || 0;
      monthlyGroups[monthKey].totalMMK += remittance.amountMMK || remittance.receiver?.amountMMK || 0;
      monthlyGroups[monthKey].transactionCount++;
    });

    return Object.values(monthlyGroups).sort(
      (a, b) => b.startDate.getTime() - a.startDate.getTime()
    );
  }, [calculateProviderSettlement, calculateKairosSettlement, getCompletedDate]);

  const pendingMonthlySettlements = useMemo(() => {
    if (!financeConfig || providerPendingData.length === 0) return [];
    return groupByMonth(providerPendingData, financeConfig);
  }, [providerPendingData, financeConfig, groupByMonth]);

  const settledMonthlySettlements = useMemo(() => {
    if (!financeConfig || providerSettledData.length === 0) return [];
    return groupByMonth(providerSettledData, financeConfig);
  }, [providerSettledData, financeConfig, groupByMonth]);

  // 하위 호환 별칭
  const pendingWeeklySettlements = pendingMonthlySettlements;
  const settledWeeklySettlements = settledMonthlySettlements;
  const groupByWeek = groupByMonth;

  useEffect(() => {
    loadSettlements();
    loadFinanceConfig();
    loadProviderAccounts();
  }, []);
  
  // 탭 변경 시 선택된 항목 초기화 및 계좌 데이터 로드
  useEffect(() => {
    setSelectedSettlements([]);
    
    // 계좌 관리 탭으로 전환 시 계좌 데이터 새로고침
    if (activeTab === 'provider-accounts') {
      loadProviderAccounts();
    }
  }, [activeTab]);

  // 월별 정산 데이터는 useMemo(pending/settledMonthlySettlements)로 계산

  // Filter and search settlements
  useEffect(() => {
    let filtered = [...settlements];

    // Search filter
    if (filters.searchTerm.trim()) {
      filtered = filtered.filter(settlement => 
        settlement.remitId?.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
        settlement.userName?.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
        settlement.userId?.toLowerCase().includes(filters.searchTerm.toLowerCase())
      );
    }

    // Status filter (settlementStatus 사용)
    if (filters.status !== 'all') {
      filtered = filtered.filter(settlement => settlement.settlementStatus === filters.status);
    }

    // Date range filter
    if (filters.dateRange !== 'all') {
      const now = new Date();
      let startDate;
      
      switch (filters.dateRange) {
        case '7days':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case '30days':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case '90days':
          startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          break;
        default:
          startDate = null;
      }
      
      if (startDate) {
        filtered = filtered.filter(settlement => {
          const completedDate = settlement.uabCompletedAt || settlement.completedAt;
          const settlementDate = completedDate?.toDate ? completedDate.toDate() : 
                                (completedDate ? new Date(completedDate) : new Date(settlement.createdAt));
          return settlementDate >= startDate;
        });
      }
    }

    // Amount range filter
    if (filters.amountRange !== 'all') {
      filtered = filtered.filter(settlement => {
        const amount = settlement.amountKRW;
        switch (filters.amountRange) {
          case '0-100000':
            return amount < 100000;
          case '100000-500000':
            return amount >= 100000 && amount < 500000;
          case '500000+':
            return amount >= 500000;
          default:
            return true;
        }
      });
    }

    setFilteredSettlements(filtered);
    setTotalSettlements(filtered.length);
    calculateStats(filtered);
  }, [settlements, filters]);

  // Calculate statistics (정산 대상 기준: COMPLETED + PENDING)
  const calculateStats = useCallback((settlementList) => {
    // settlementList는 이미 정산 대상만 포함 (COMPLETED + PENDING)
    const stats = {
      total: settlementList.length, // 전체 정산 대상 건수
      pending: settlementList.filter(s => {
        const status = s.settlementStatus !== undefined ? s.settlementStatus : 'PENDING';
        return status === 'PENDING';
      }).length, // 정산 대기 중
      settled: settlementList.filter(s => s.settlementStatus === 'SETTLED').length, // 정산 완료
      failed: settlementList.filter(s => s.settlementStatus === 'FAILED').length,
      processing: 0, // settlementStatus에는 PROCESSING이 없음
      totalAmount: settlementList.reduce((sum, s) => sum + (s.amountKRW || 0), 0),
      totalFees: settlementList.reduce((sum, s) => sum + (s.feeKRW || 0), 0),
      totalMMK: settlementList.reduce((sum, s) => sum + (s.amountMMK || 0), 0),
      totalUSD: settlementList.reduce((sum, s) => sum + (s.amountUSD || 0), 0)
    };
    setStats(stats);
  }, []);

  // 금융설정 로드
  // 금융설정 로드 (송금업체 정산 탭에서만 필요)
  const loadFinanceConfig = async () => {
    try {
      setFinanceLoading(true);
      // settings/finance (송금·정산 공통) 우선
      const financeDocRef = doc(db, `artifacts/${appId}/settings/finance`);
      const financeSnap = await getDoc(financeDocRef);

      if (financeSnap.exists()) {
        setFinanceConfig(financeSnap.data());
        return;
      }

      const financeRef = collection(db, `artifacts/${appId}/finance_config`);
      const snapshot = await getDocs(financeRef);

      if (!snapshot.empty) {
        setFinanceConfig(snapshot.docs[0].data());
      } else {
        setFinanceConfig({
          feeSettings: {
            remittanceFeeRatePercent: 2.5,
            kairosSharePercent: 1.5,
            hectoSharePercent: 1.0,
          },
          settlementConfig: {
            kairosSharePercent: 1.5,
            hectoSharePercent: 1.0,
          },
        });
      }
    } catch (error) {
      console.error('금융설정 로드 실패:', error);
      showToastMessage('금융설정을 불러오는데 실패했습니다.', 'error');
    } finally {
      setFinanceLoading(false);
    }
  };

  // 실제 정산 데이터 로드 (캐시 우선 방식)
  const loadSettlements = async () => {
    try {
      setLoading(true);
      console.log('🔍 정산 데이터 로드 시작');
      
      let settlementsData = [];
      let statsFromCache = null;
      
      // 1. 캐시 컬렉션에서 데이터 로드 시도
      try {
        console.log('💾 캐시 데이터 로드 시도...');
        
        // 타임아웃 설정 (10초)
        const cacheLoadPromise = (async () => {
          try {
            // 통계 데이터 로드
            // artifacts/{appId}는 문서, admin은 서브컬렉션
            const artifactsDocRef = doc(db, `artifacts/${appId}`);
            const adminCollectionRef = collection(artifactsDocRef, 'admin');
            const adminDocRef = doc(adminCollectionRef, 'admin');
            const statsDocRef = doc(adminCollectionRef, 'settlementStats');
            const statsDoc = await getDocFromServer(statsDocRef);
            
            if (statsDoc.exists()) {
              statsFromCache = statsDoc.data();
              const lastUpdated = statsFromCache.lastUpdated?.toDate ? statsFromCache.lastUpdated.toDate() : new Date(statsFromCache.lastUpdated);
              const hoursAgo = (Date.now() - lastUpdated.getTime()) / (1000 * 60 * 60);
              console.log(`📊 캐시 통계 발견 (${hoursAgo.toFixed(1)}시간 전 업데이트)`);
            }
            
            // 정산 대상 거래 캐시 로드
            // settlementCache는 admin 문서 아래 서브컬렉션
            const cacheRef = collection(adminDocRef, 'settlementCache');
            const cacheSnapshot = await getDocs(cacheRef);
            
            console.log(`📊 캐시에서 ${cacheSnapshot.docs.length}건의 정산 대상 거래 로드`);
            
            settlementsData = cacheSnapshot.docs.map(docSnap => {
              const data = docSnap.data();
              return {
                id: docSnap.id,
                remitId: data.remitId || docSnap.id,
                userId: data.userId || '',
                userName: data.userName || '',
                amountKRW: data.amountKRW || 0,
                amountUSD: data.amountUSD || 0,
                amountMMK: data.amountMMK || 0,
                feeKRW: data.feeKRW || 0,
                rates: data.rates || {},
                exchangeRate: data.exchangeRate || 0,
                finalExchangeRate: data.finalExchangeRate || {},
                providerAccount: {},
                receiver: {},
                uabCompletedAt: data.uabCompletedAt?.toDate ? data.uabCompletedAt.toDate() : 
                              (data.uabCompletedAt ? new Date(data.uabCompletedAt) : null),
                completedAt: data.completedAt?.toDate ? data.completedAt.toDate() : 
                            (data.completedAt ? new Date(data.completedAt) : null),
                createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : 
                          (data.createdAt ? new Date(data.createdAt) : new Date()),
                settlementStatus: data.settlementStatus || 'PENDING',
                status: data.status || 'COMPLETED',
                providerSettlement: data.providerSettlement || 0,
                kairosSettlement: data.kairosSettlement || 0,
                source: 'cache'
              };
            });
            
            console.log('✅ 캐시 데이터 로드 완료');
            return true;
          } catch (error) {
            console.error('⚠️ 캐시 로드 내부 오류:', error);
            throw error;
          }
        })();
        
        // 10초 타임아웃
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('캐시 로드 타임아웃')), 10000)
        );
        
        await Promise.race([cacheLoadPromise, timeoutPromise]);
        
        if (settlementsData.length > 0) {
          console.log(`✅ 캐시에서 ${settlementsData.length}건의 정산 데이터 로드 완료`);

          // 캐시는 주로 PENDING만 있으므로 SETTLED를 별도 조회해 병합
          try {
            const settledQ = query(
              collection(db, `artifacts/${appId}/remittances`),
              where('status', '==', 'COMPLETED'),
              where('settlementStatus', '==', 'SETTLED')
            );
            const settledSnap = await getDocs(settledQ);
            const existingIds = new Set(settlementsData.map((s) => s.remitId || s.id));
            settledSnap.docs.forEach((docSnap) => {
              const data = docSnap.data();
              const remitId = data.remitId || docSnap.id;
              if (existingIds.has(remitId)) return;
              settlementsData.push({
                id: docSnap.id,
                remitId,
                userId: data.userId || '',
                userName: data.userName || '',
                amountKRW: data.amountKRW || 0,
                amountUSD: data.amountUSD || 0,
                amountMMK: data.amountMMK || 0,
                feeKRW: data.feeKRW || 0,
                grossPaymentKRW: data.grossPaymentKRW || data.totalDepositKRW || 0,
                rates: data.rates || {},
                uabCompletedAt: data.uabCompletedAt?.toDate
                  ? data.uabCompletedAt.toDate()
                  : data.uabCompletedAt
                    ? new Date(data.uabCompletedAt)
                    : null,
                completedAt: data.completedAt?.toDate
                  ? data.completedAt.toDate()
                  : data.completedAt
                    ? new Date(data.completedAt)
                    : null,
                createdAt: data.createdAt?.toDate
                  ? data.createdAt.toDate()
                  : data.createdAt
                    ? new Date(data.createdAt)
                    : new Date(),
                settledAt: data.settledAt?.toDate
                  ? data.settledAt.toDate()
                  : data.settledAt
                    ? new Date(data.settledAt)
                    : null,
                settlementStatus: 'SETTLED',
                status: 'COMPLETED',
                source: 'settled-query',
              });
            });
          } catch (settledErr) {
            console.warn('⚠️ SETTLED 병합 조회 실패(캐시 PENDING만 표시):', settledErr.message);
          }
          
          // 통계 데이터 업데이트
          if (statsFromCache) {
            setStats({
              total: statsFromCache.totalPending || settlementsData.length,
              pending: statsFromCache.totalPending || settlementsData.length,
              settled: statsFromCache.totalSettled || settlementsData.filter((s) => s.settlementStatus === 'SETTLED').length,
              failed: 0,
              processing: 0,
              totalAmount: statsFromCache.totalAmountKRW || 0,
              totalFees: statsFromCache.totalFeesKRW || 0,
              totalMMK: 0,
              totalUSD: statsFromCache.totalAmountUSD || 0
            });
          }
          
          // 가장 이전 것부터 정렬 (createdAt 오름차순)
          settlementsData.sort((a, b) => {
            const dateA = getDateTimestamp(a.createdAt) || 0;
            const dateB = getDateTimestamp(b.createdAt) || 0;
            return dateA - dateB;
          });
          
          setSettlements(settlementsData);
          setLoading(false);
          return;
        }
      } catch (error) {
        console.warn('⚠️ 캐시 로드 실패, 실시간 계산으로 폴백:', error.message);
      }
      
      // 2. 폴백: 실시간 계산 (기존 로직)
      console.log('📊 실시간 데이터 로드 시작 (폴백)...');
      
      // 🔥 개선된 중복 방지: remitId뿐만 아니라 userId + amount + createdAt 조합으로 중복 체크
      const processedRemitIds = new Set(); // remitId 기반 중복 체크
      const processedUniqueKeys = new Set(); // 고유 키 기반 중복 체크 (remitId가 다른 경우)
      settlementsData = [];
      
      // 고유 키 생성 함수
      const getUniqueKey = (data, remitId) => {
        const userId = data.userId || '';
        const amountUSD = Number(data.amountUSD || data.receiver?.amountUSD || data.actualAmountUSD || 0);
        const createdAt = data.createdAt ? (data.createdAt?.toDate ? data.createdAt.toDate().getTime() : new Date(data.createdAt).getTime()) : 0;
        return `${userId}_${amountUSD}_${createdAt}`;
      };
      
      // 1. 메인 remittances 컬렉션에서 정산 대상 거래 로드
      // 정산 대상: status === 'COMPLETED' AND settlementStatus === 'PENDING' (또는 없음)
      try {
        console.log('📊 1단계: 메인 remittances 컬렉션 조회 중 (정산 대상: COMPLETED + PENDING)...');
        const remittancesRef = collection(db, `artifacts/${appId}/remittances`);
        
        // Firestore 복합 쿼리 제한: status와 settlementStatus를 동시에 필터링할 수 없으므로
        // 먼저 COMPLETED만 조회 후 클라이언트에서 settlementStatus 필터링
        let q;
        try {
          // 복합 쿼리 시도 (인덱스가 있는 경우)
          q = query(
            remittancesRef,
            where('status', '==', 'COMPLETED'),
            where('settlementStatus', '==', 'PENDING'),
            orderBy('uabCompletedAt', 'desc')
          );
        } catch (queryError) {
          // 복합 쿼리 실패 시 COMPLETED만 조회 후 클라이언트에서 필터링
          console.warn('⚠️ 복합 쿼리 실패, COMPLETED만 조회 후 클라이언트 필터링:', queryError.message);
          q = query(
            remittancesRef,
            where('status', '==', 'COMPLETED'),
            orderBy('uabCompletedAt', 'desc')
          );
        }
        
        const snapshot = await getDocs(q);
        
        for (const docSnap of snapshot.docs) {
          const data = docSnap.data();
          const remitId = data.remitId || docSnap.id;
          const uniqueKey = getUniqueKey(data, remitId);
          
          // 정산 전(PENDING) + 정산 완료(SETTLED) 모두 로드
          const settlementStatus = data.settlementStatus !== undefined ? data.settlementStatus : 'PENDING';
          if (settlementStatus !== 'PENDING' && settlementStatus !== 'SETTLED') {
            continue;
          }
          
          // Firestore에서 settlementStatus 필드 확인 (로깅)
          if (settlementsData.length === 0) {
            console.log('📋 [Firestore 확인] 메인 컬렉션 첫 번째 문서의 settlementStatus 필드:', {
              remitId: remitId,
              hasSettlementStatus: 'settlementStatus' in data,
              settlementStatusValue: data.settlementStatus,
              settlementStatusType: typeof data.settlementStatus,
              allKeys: Object.keys(data).filter(k => k.includes('settlement') || k.includes('settled'))
            });
          }
          
          // 🔥 개선된 중복 체크: remitId와 고유 키 모두 확인
          if (processedRemitIds.has(remitId) || processedUniqueKeys.has(uniqueKey)) {
            if (process.env.NODE_ENV === 'development') {
              console.log(`⚠️ 중복된 정산 데이터 발견: remitId=${remitId}, uniqueKey=${uniqueKey}`);
            }
            continue;
          }
            settlementsData.push({
              id: docSnap.id,
              remitId: remitId,
              userId: data.userId,
              userName: data.userName,
              amountKRW: data.amountKRW || 0,
              amountUSD: data.amountUSD || 0,
              amountMMK: data.amountMMK || 0,
              feeKRW: data.feeKRW || 0,
              rates: data.rates || {},
              providerAccount: data.providerAccount || {},
              receiver: data.receiver || {},
              uabCompletedAt: data.uabCompletedAt?.toDate ? data.uabCompletedAt.toDate() : 
                            (data.uabCompletedAt ? new Date(data.uabCompletedAt) : null),
              uabTransactionId: data.uabTransactionId,
              actualAmountMMK: data.actualAmountMMK,
              finalExchangeRate: data.finalExchangeRate || {},
              settlementStatus: data.settlementStatus !== undefined ? data.settlementStatus : 'PENDING', // Firestore에서 settlementStatus 필드 확인
              settledAt: data.settledAt?.toDate ? data.settledAt.toDate() : 
                        (data.settledAt ? new Date(data.settledAt) : null),
              settledBy: data.settledBy,
              settlementNotes: data.settlementNotes || '',
              createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
              completedAt: data.completedAt?.toDate ? data.completedAt.toDate() : 
                          (data.completedAt ? new Date(data.completedAt) : null),
              status: data.status || 'COMPLETED',
              source: 'main_collection' // 데이터 출처 표시
            });
            processedRemitIds.add(remitId);
            processedUniqueKeys.add(uniqueKey);
        }
        console.log(`✅ 메인 컬렉션에서 ${snapshot.docs.length}건 조회 완료`);
      } catch (error) {
        console.warn('⚠️ 메인 컬렉션 조회 실패:', error.message);
      }
      
      // 2. 모든 사용자의 remittances 서브컬렉션에서 COMPLETED 거래 로드
      try {
        console.log('📊 2단계: 모든 사용자별 송금 컬렉션 조회 중...');
        const usersRef = collection(db, `artifacts/${appId}/users`);
        const usersSnapshot = await getDocs(usersRef);
        
        console.log(`📋 총 ${usersSnapshot.docs.length}명의 사용자 확인`);
        
        let userRemittancesCount = 0;
        const batchSize = 10; // 배치 처리로 성능 최적화
        
        for (let i = 0; i < usersSnapshot.docs.length; i += batchSize) {
          const batch = usersSnapshot.docs.slice(i, i + batchSize);
          
          const batchPromises = batch.map(async (userDoc) => {
            const userId = userDoc.id;
            try {
              // 사용자별 remittances 서브컬렉션 조회
              const userRemittancesRef = collection(db, `artifacts/${appId}/users/${userId}/remittances`);
              
              // COMPLETED 상태 필터링 시도
              let q;
              try {
                q = query(
                  userRemittancesRef,
                  where('status', '==', 'COMPLETED'),
                  orderBy('uabCompletedAt', 'desc')
                );
              } catch (queryError) {
                // 인덱스가 없거나 orderBy 실패 시 상태 필터만 적용
                try {
                  q = query(userRemittancesRef, where('status', '==', 'COMPLETED'));
                } catch (filterError) {
                  // 상태 필터도 실패하면 전체 조회 후 클라이언트에서 필터링
                  q = query(userRemittancesRef);
                }
              }
              
              const userRemittancesSnapshot = await getDocs(q);
              
              const userRemittances = [];
              for (const remitDoc of userRemittancesSnapshot.docs) {
                const remitData = remitDoc.data();
                
              // COMPLETED + (PENDING|SETTLED)
              if (remitData.status !== 'COMPLETED') {
                continue;
              }
              
              const settlementStatus = remitData.settlementStatus !== undefined ? remitData.settlementStatus : 'PENDING';
              if (settlementStatus !== 'PENDING' && settlementStatus !== 'SETTLED') {
                continue;
              }
              
              const remitId = remitData.remitId || remitDoc.id;
              const uniqueKey = getUniqueKey(remitData, remitId);
              
              // Firestore에서 settlementStatus 필드 확인 (로깅 - 첫 번째 문서만)
              if (userRemittances.length === 0 && settlementsData.length === 0) {
                console.log('📋 [Firestore 확인] 사용자별 서브컬렉션 첫 번째 문서의 settlementStatus 필드:', {
                  userId: userId,
                  remitId: remitId,
                  hasSettlementStatus: 'settlementStatus' in remitData,
                  settlementStatusValue: remitData.settlementStatus,
                  settlementStatusType: typeof remitData.settlementStatus,
                  allKeys: Object.keys(remitData).filter(k => k.includes('settlement') || k.includes('settled'))
                });
              }
              
              // 🔥 개선된 중복 방지: remitId와 고유 키 모두 확인 (이미 메인 컬렉션에서 가져온 경우 스킵)
              if (processedRemitIds.has(remitId) || processedUniqueKeys.has(uniqueKey)) {
                if (process.env.NODE_ENV === 'development') {
                  console.log(`⚠️ 중복된 정산 데이터 발견 (사용자별 컬렉션): remitId=${remitId}, uniqueKey=${uniqueKey}`);
                }
                continue;
              }
                  userRemittances.push({
                    id: remitDoc.id,
                    remitId: remitId,
                    userId: userId,
                    userName: remitData.userName || remitData.user_name || userDoc.data().name || '',
                    amountKRW: remitData.amountKRW || remitData.amountKRW || 0,
                    amountUSD: remitData.amountUSD || remitData.amountUSD || 0,
                    amountMMK: remitData.amountMMK || remitData.amountMMK || 0,
                    feeKRW: remitData.feeKRW || remitData.fee || 0,
                    rates: remitData.rates || remitData.exchangeRate || {},
                    providerAccount: remitData.providerAccount || remitData.remittancePartner || {},
                    receiver: remitData.receiver || remitData.recipientInfo || {},
                    uabCompletedAt: remitData.uabCompletedAt?.toDate ? remitData.uabCompletedAt.toDate() : 
                                  (remitData.uabCompletedAt ? new Date(remitData.uabCompletedAt) :
                                  (remitData.completedAt?.toDate ? remitData.completedAt.toDate() :
                                  (remitData.completedAt ? new Date(remitData.completedAt) : null))),
                    uabTransactionId: remitData.uabTransactionId || remitData.transactionId,
                    actualAmountMMK: remitData.actualAmountMMK || remitData.amountMMK || 0,
                    finalExchangeRate: remitData.finalExchangeRate || remitData.exchangeRate || {},
                    settlementStatus: remitData.settlementStatus !== undefined ? remitData.settlementStatus : 'PENDING', // Firestore에서 settlementStatus 필드 확인
                    settledAt: remitData.settledAt?.toDate ? remitData.settledAt.toDate() : 
                              (remitData.settledAt ? new Date(remitData.settledAt) : null),
                    settledBy: remitData.settledBy,
                    settlementNotes: remitData.settlementNotes || '',
                    createdAt: remitData.createdAt?.toDate ? remitData.createdAt.toDate() : 
                              (remitData.createdAt ? new Date(remitData.createdAt) : new Date()),
                    completedAt: remitData.completedAt?.toDate ? remitData.completedAt.toDate() : 
                                (remitData.completedAt ? new Date(remitData.completedAt) : null),
                    status: remitData.status || 'COMPLETED',
                    source: 'user_subcollection' // 데이터 출처 표시
                  });
                  processedRemitIds.add(remitId);
                  processedUniqueKeys.add(uniqueKey);
              }
              
              return userRemittances;
            } catch (error) {
              console.warn(`⚠️ 사용자 ${userId} 송금 데이터 조회 실패:`, error.message);
              return [];
            }
          });
          
          const batchResults = await Promise.all(batchPromises);
          const flatResults = batchResults.flat();
          settlementsData.push(...flatResults);
          userRemittancesCount += flatResults.length;
          
          console.log(`📊 사용자 배치 처리 완료: ${Math.min(i + batchSize, usersSnapshot.docs.length)}/${usersSnapshot.docs.length} (누적: ${userRemittancesCount}건)`);
        }
        
        console.log(`✅ 사용자별 컬렉션에서 추가 ${userRemittancesCount}건 조회 완료`);
      } catch (error) {
        console.warn('⚠️ 사용자별 컬렉션 조회 실패:', error.message);
      }
      
      // 3. 최종 데이터 정렬 (uabCompletedAt 기준 내림차순)
      // 가장 이전 것부터 정렬 (createdAt 오름차순)
      settlementsData.sort((a, b) => {
        const dateA = getDateTimestamp(a.createdAt) || 0;
        const dateB = getDateTimestamp(b.createdAt) || 0;
        return dateA - dateB;
      });
      
      setSettlements(settlementsData);
      
      console.log('✅ 정산 데이터 로드 완료:', settlementsData.length, '건 (정산 대상: COMPLETED + PENDING)');
      console.log(`📊 데이터 출처: 캐시(${settlementsData.filter(s => s.source === 'cache').length}건), 실시간(${settlementsData.filter(s => s.source !== 'cache').length}건)`);
      console.log(`📊 데이터 출처: 메인 컬렉션 ${settlementsData.filter(s => s.source === 'main_collection').length}건, 사용자별 컬렉션 ${settlementsData.filter(s => s.source === 'user_subcollection').length}건`);
      console.log(`📊 정산 상태별: PENDING ${settlementsData.filter(s => (s.settlementStatus !== undefined ? s.settlementStatus : 'PENDING') === 'PENDING').length}건, SETTLED ${settlementsData.filter(s => s.settlementStatus === 'SETTLED').length}건`);
    } catch (error) {
      console.error('❌ 정산 데이터 로드 실패:', error);
      showToastMessage('정산 데이터를 불러오는데 실패했습니다.', 'error');
      
      // 에러 발생 시 빈 배열로 설정
      setSettlements([]);
    } finally {
      setLoading(false);
    }
  };

  // 송금업체 계좌 로드
  const loadProviderAccounts = async () => {
    try {
      setProviderLoading(true);
      console.log('🔍 송금업체 계좌 로드 시작');
      
      const accounts = await providerAccountService.getAllAccounts();
      
      // usageCount와 totalAmount가 없는 경우 기본값 설정
      const accountsWithDefaults = accounts.map(account => ({
        ...account,
        usageCount: account.usageCount || 0,
        totalAmount: account.totalAmount || 0,
        dailyLimit: account.dailyLimit || 0,
        monthlyLimit: account.monthlyLimit || 0
      }));
      
      setProviderAccounts(accountsWithDefaults);
      
      console.log('✅ 송금업체 계좌 로드 완료:', accountsWithDefaults.length, '개');
      
      if (accountsWithDefaults.length === 0) {
        showToastMessage('등록된 계좌가 없습니다.', 'info');
      }
    } catch (error) {
      console.error('❌ 송금업체 계좌 로드 실패:', error);
      showToastMessage('계좌 정보를 불러오는 중 오류가 발생했습니다.', 'error');
      // 에러 발생 시 빈 배열로 설정
      setProviderAccounts([]);
    } finally {
      setProviderLoading(false);
    }
  };

  // 실제 정산 처리 (remittances 컬렉션의 settlementStatus 업데이트)
  const handleSettlement = async (remitId) => {
    try {
      console.log('💳 정산 처리 시작:', remitId);
      
      // 이미 정산 완료된 경우 확인
      const settlement = settlements.find(s => s.id === remitId || s.remitId === remitId);
      if (!settlement) {
        showToastMessage('송금 거래를 찾을 수 없습니다.', 'error');
        return;
      }
      
      if (settlement.settlementStatus === 'SETTLED') {
        showToastMessage('이미 정산이 완료된 거래입니다.', 'warning');
        return;
      }
      
      if (settlement.status !== 'COMPLETED') {
        showToastMessage('송금이 완료되지 않은 거래는 정산할 수 없습니다.', 'error');
        return;
      }
      
      // Firestore에서 정산 상태 업데이트 (메인 컬렉션과 사용자별 서브컬렉션 모두 업데이트)
      const updateData = {
        settlementStatus: 'SETTLED',
        settledAt: serverTimestamp(),
        settledBy: 'admin', // TODO: 실제 관리자 ID로 변경
        updatedAt: serverTimestamp()
      };
      
      // 1. 메인 remittances 컬렉션 업데이트
      const remittanceDocRef = doc(db, `artifacts/${appId}/remittances`, settlement.id);
      await updateDoc(remittanceDocRef, updateData);
      console.log('✅ 메인 컬렉션 정산 상태 업데이트 완료');
      
      // 2. 사용자별 서브컬렉션도 업데이트 (데이터 일관성 유지)
      if (settlement.userId) {
        try {
          const userRemittanceDocRef = doc(db, `artifacts/${appId}/users/${settlement.userId}/remittances`, settlement.id);
          await updateDoc(userRemittanceDocRef, updateData);
          console.log('✅ 사용자별 서브컬렉션 정산 상태 업데이트 완료');
        } catch (userUpdateError) {
          // 사용자별 서브컬렉션에 문서가 없을 수 있으므로 경고만 출력
          console.warn('⚠️ 사용자별 서브컬렉션 업데이트 실패 (문서가 없을 수 있음):', userUpdateError.message);
        }
      }
      
      // 3. 캐시 업데이트: settlementCache에서 제거하고 settledRemittancesCache에 추가
      try {
        // settlementCache에서 제거
        const artifactsDocRef = doc(db, `artifacts/${appId}`);
        const adminCollectionRef = collection(artifactsDocRef, 'admin');
        const settlementCacheRef = collection(adminCollectionRef, 'settlementCache');
        const cacheDocRef = doc(settlementCacheRef, settlement.remitId || remitId);
        await deleteDoc(cacheDocRef);
        console.log('✅ 정산 대상 캐시에서 제거 완료');
        
        // settledRemittancesCache에 추가
        const settledCacheCollectionRef = collection(adminCollectionRef, 'settledRemittancesCache');
        const settledCacheDocRef = doc(settledCacheCollectionRef, settlement.remitId || remitId);
        const settledData = {
          remitId: settlement.remitId || remitId,
          userId: settlement.userId || '',
          userName: settlement.userName || '',
          status: 'COMPLETED',
          settlementStatus: 'SETTLED',
          amountKRW: settlement.amountKRW || 0,
          amountUSD: settlement.amountUSD || 0,
          amountMMK: settlement.amountMMK || 0,
          feeKRW: settlement.feeKRW || 0,
          rates: settlement.rates || {},
          exchangeRate: settlement.exchangeRate || 0,
          finalExchangeRate: settlement.finalExchangeRate || {},
          uabCompletedAt: settlement.uabCompletedAt,
          completedAt: settlement.completedAt,
          createdAt: settlement.createdAt,
          settledAt: serverTimestamp(),
          settledBy: 'admin',
          providerSettlement: calculateProviderSettlement(settlement, financeConfig),
          kairosSettlement: calculateKairosSettlement(settlement, financeConfig),
          sourceCollection: settlement.sourceCollection || 'main',
          sourcePath: settlement.sourcePath || '',
          lastUpdated: serverTimestamp()
        };
        await setDoc(settledCacheDocRef, settledData, { merge: true });
        console.log('✅ 정산 완료 캐시에 추가 완료');
      } catch (cacheError) {
        console.warn('⚠️ 캐시 업데이트 실패 (계속 진행):', cacheError.message);
      }
      
      // 로컬 상태 업데이트
      setSettlements(prev => 
        prev.map(s => 
          (s.id === remitId || s.remitId === remitId)
            ? { ...s, settlementStatus: 'SETTLED', settledAt: new Date() }
            : s
        )
      );
      
      showToastMessage('정산이 성공적으로 처리되었습니다.', 'success');
      setShowSettlementModal(false);
      console.log('✅ 정산 처리 완료');
      
      // 선택된 항목에서 제거
      setSelectedSettlements(prev => prev.filter(id => id !== remitId));
      
      // 데이터 새로고침
      await loadSettlements();
    } catch (error) {
      console.error('❌ 정산 처리 실패:', error);
      showToastMessage(`정산 처리에 실패했습니다: ${error.message}`, 'error');
    }
  };
  
  // 일괄 정산 처리
  const handleBatchSettlement = async () => {
    if (selectedSettlements.length === 0) {
      showToastMessage('정산 처리할 항목을 선택해주세요.', 'warning');
      return;
    }
    
    const pendingItems = selectedSettlements.filter(id => {
      const settlement = settlements.find(s => (s.id || s.remitId) === id);
      const status = settlement?.settlementStatus !== undefined ? settlement.settlementStatus : 'PENDING';
      return status === 'PENDING';
    });
    
    if (pendingItems.length === 0) {
      showToastMessage('선택한 항목 중 정산 대기 상태인 항목이 없습니다.', 'warning');
      return;
    }
    
    if (!window.confirm(`선택한 ${pendingItems.length}건의 거래를 정산 완료 처리하시겠습니까?`)) {
      return;
    }
    
    try {
      setBatchSettling(true);
      console.log(`💳 일괄 정산 처리 시작: ${pendingItems.length}건`);
      
      let successCount = 0;
      let failCount = 0;
      
      // 각 항목을 순차적으로 처리
      for (const remitId of pendingItems) {
        try {
          const settlement = settlements.find(s => s.id === remitId || s.remitId === remitId);
          if (!settlement) {
            console.warn(`⚠️ 거래를 찾을 수 없음: ${remitId}`);
            failCount++;
            continue;
          }
          
          if (settlement.settlementStatus === 'SETTLED') {
            console.log(`⏭️ 이미 정산 완료: ${remitId}`);
            continue;
          }
          
          if (settlement.status !== 'COMPLETED') {
            console.warn(`⚠️ 송금 미완료: ${remitId}`);
            failCount++;
            continue;
          }
          
          // Firestore에서 정산 상태 업데이트
          const updateData = {
            settlementStatus: 'SETTLED',
            settledAt: serverTimestamp(),
            settledBy: 'admin',
            updatedAt: serverTimestamp()
          };
          
          // 메인 컬렉션 업데이트
          const remittanceDocRef = doc(db, `artifacts/${appId}/remittances`, settlement.id);
          await updateDoc(remittanceDocRef, updateData);
          
          // 사용자별 서브컬렉션 업데이트
          if (settlement.userId) {
            try {
              const userRemittanceDocRef = doc(db, `artifacts/${appId}/users/${settlement.userId}/remittances`, settlement.id);
              await updateDoc(userRemittanceDocRef, updateData);
            } catch (userUpdateError) {
              console.warn(`⚠️ 사용자 서브컬렉션 업데이트 실패: ${remitId}`, userUpdateError.message);
            }
          }
          
          // 캐시 업데이트
          try {
            const artifactsDocRef = doc(db, `artifacts/${appId}`);
            const adminCollectionRef = collection(artifactsDocRef, 'admin');
            const settlementCacheRef = collection(adminCollectionRef, 'settlementCache');
            const cacheDocRef = doc(settlementCacheRef, settlement.remitId || remitId);
            await deleteDoc(cacheDocRef);
            
            const settledCacheCollectionRef = collection(adminCollectionRef, 'settledRemittancesCache');
            const settledCacheDocRef = doc(settledCacheCollectionRef, settlement.remitId || remitId);
            const settledData = {
              remitId: settlement.remitId || remitId,
              userId: settlement.userId || '',
              userName: settlement.userName || '',
              status: 'COMPLETED',
              settlementStatus: 'SETTLED',
              amountKRW: settlement.amountKRW || 0,
              amountUSD: settlement.amountUSD || 0,
              amountMMK: settlement.amountMMK || 0,
              feeKRW: settlement.feeKRW || 0,
              rates: settlement.rates || {},
              exchangeRate: settlement.exchangeRate || 0,
              finalExchangeRate: settlement.finalExchangeRate || {},
              uabCompletedAt: settlement.uabCompletedAt,
              completedAt: settlement.completedAt,
              createdAt: settlement.createdAt,
              settledAt: serverTimestamp(),
              settledBy: 'admin',
              providerSettlement: calculateProviderSettlement(settlement, financeConfig),
              kairosSettlement: calculateKairosSettlement(settlement, financeConfig),
              sourceCollection: settlement.sourceCollection || 'main',
              sourcePath: settlement.sourcePath || '',
              lastUpdated: serverTimestamp()
            };
            await setDoc(settledCacheDocRef, settledData, { merge: true });
          } catch (cacheError) {
            console.warn(`⚠️ 캐시 업데이트 실패: ${remitId}`, cacheError.message);
          }
          
          successCount++;
          console.log(`✅ ${remitId} 정산 처리 완료 (${successCount}/${pendingItems.length})`);
        } catch (error) {
          console.error(`❌ ${remitId} 정산 처리 실패:`, error.message);
          failCount++;
        }
      }
      
      console.log(`✅ 일괄 정산 처리 완료: 성공 ${successCount}건, 실패 ${failCount}건`);
      showToastMessage(`일괄 정산 처리 완료: 성공 ${successCount}건${failCount > 0 ? `, 실패 ${failCount}건` : ''}`, 'success');
      
      // 선택된 항목 초기화
      setSelectedSettlements([]);
      
      // 데이터 새로고침
      await loadSettlements();
    } catch (error) {
      console.error('❌ 일괄 정산 처리 실패:', error);
      showToastMessage('일괄 정산 처리에 실패했습니다: ' + error.message, 'error');
    } finally {
      setBatchSettling(false);
    }
  };

  // 월별 일괄 정산 처리
  const handleMonthlySettlement = async (monthGroup) => {
    if (!monthGroup?.remittances?.length) {
      showToastMessage('정산할 거래가 없습니다.', 'warning');
      return;
    }

    const pendingRemittances = monthGroup.remittances.filter((r) => {
      const status = r.settlementStatus !== undefined ? r.settlementStatus : 'PENDING';
      return status === 'PENDING' && r.status === 'COMPLETED';
    });

    if (pendingRemittances.length === 0) {
      showToastMessage('해당 월에 정산 대기 거래가 없습니다.', 'warning');
      return;
    }

    const monthLabel = monthGroup.monthKey || dayjs(monthGroup.startDate).format('YYYY-MM');
    const kairosTotal = pendingRemittances.reduce(
      (sum, r) => sum + calculateKairosSettlement(r, financeConfig),
      0
    );
    const hectoTotal = pendingRemittances.reduce(
      (sum, r) => sum + calculateProviderSettlement(r, financeConfig),
      0
    );

    if (
      !window.confirm(
        `${monthLabel}월 정산을 완료할까요?\n\n` +
          `대상: ${pendingRemittances.length}건\n` +
          `Kairos: ${kairosTotal.toLocaleString()}원\n` +
          `헥토: ${hectoTotal.toLocaleString()}원`
      )
    ) {
      return;
    }

    try {
      setBatchSettling(true);
      setSettlingMonthKey(monthLabel);
      let successCount = 0;
      let failCount = 0;

      for (const settlement of pendingRemittances) {
        const remitId = settlement.id || settlement.remitId;
        try {
          if (settlement.settlementStatus === 'SETTLED') continue;
          if (settlement.status !== 'COMPLETED') {
            failCount++;
            continue;
          }

          const updateData = {
            settlementStatus: 'SETTLED',
            settledAt: serverTimestamp(),
            settledBy: 'admin',
            settlementMonth: monthLabel,
            updatedAt: serverTimestamp(),
          };

          const remittanceDocRef = doc(db, `artifacts/${appId}/remittances`, settlement.id);
          await updateDoc(remittanceDocRef, updateData);

          if (settlement.userId) {
            try {
              const userRemittanceDocRef = doc(
                db,
                `artifacts/${appId}/users/${settlement.userId}/remittances`,
                settlement.id
              );
              await updateDoc(userRemittanceDocRef, updateData);
            } catch (userUpdateError) {
              console.warn(`⚠️ 사용자 서브컬렉션 업데이트 실패: ${remitId}`, userUpdateError.message);
            }
          }

          try {
            const artifactsDocRef = doc(db, `artifacts/${appId}`);
            const adminCollectionRef = collection(artifactsDocRef, 'admin');
            const settlementCacheRef = collection(adminCollectionRef, 'settlementCache');
            const cacheDocRef = doc(settlementCacheRef, settlement.remitId || remitId);
            await deleteDoc(cacheDocRef);

            const settledCacheCollectionRef = collection(adminCollectionRef, 'settledRemittancesCache');
            const settledCacheDocRef = doc(settledCacheCollectionRef, settlement.remitId || remitId);
            await setDoc(
              settledCacheDocRef,
              {
                remitId: settlement.remitId || remitId,
                userId: settlement.userId || '',
                userName: settlement.userName || '',
                status: 'COMPLETED',
                settlementStatus: 'SETTLED',
                amountKRW: settlement.amountKRW || 0,
                amountUSD: settlement.amountUSD || 0,
                amountMMK: settlement.amountMMK || 0,
                feeKRW: settlement.feeKRW || 0,
                rates: settlement.rates || {},
                exchangeRate: settlement.exchangeRate || 0,
                finalExchangeRate: settlement.finalExchangeRate || {},
                uabCompletedAt: settlement.uabCompletedAt,
                completedAt: settlement.completedAt,
                createdAt: settlement.createdAt,
                settledAt: serverTimestamp(),
                settledBy: 'admin',
                settlementMonth: monthLabel,
                providerSettlement: calculateProviderSettlement(settlement, financeConfig),
                kairosSettlement: calculateKairosSettlement(settlement, financeConfig),
                sourceCollection: settlement.sourceCollection || 'main',
                sourcePath: settlement.sourcePath || '',
                lastUpdated: serverTimestamp(),
              },
              { merge: true }
            );
          } catch (cacheError) {
            console.warn(`⚠️ 캐시 업데이트 실패: ${remitId}`, cacheError.message);
          }

          successCount++;
        } catch (error) {
          console.error(`❌ ${remitId} 월 정산 실패:`, error.message);
          failCount++;
        }
      }

      showToastMessage(
        `${monthLabel} 월 정산 완료: 성공 ${successCount}건${failCount > 0 ? `, 실패 ${failCount}건` : ''}`,
        failCount > 0 ? 'warning' : 'success'
      );
      setShowWeekDetails(false);
      setSelectedWeek(null);
      await loadSettlements();
    } catch (error) {
      console.error('❌ 월 정산 실패:', error);
      showToastMessage('월 정산에 실패했습니다: ' + error.message, 'error');
    } finally {
      setBatchSettling(false);
      setSettlingMonthKey(null);
    }
  };

  // 페이지네이션된 정산 데이터
  const getPaginatedSettlements = () => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredSettlements.slice(startIndex, endIndex);
  };

  // 정산 상태 정보 (settlementStatus 사용)
  const getSettlementStatusInfo = (settlementStatus) => {
    const statusMap = {
      PENDING: { color: 'warning', text: '정산 대기', icon: <Clock className="w-3 h-3" /> },
      SETTLED: { color: 'success', text: '정산 완료', icon: <CheckCircle className="w-3 h-3" /> },
      FAILED: { color: 'error', text: '정산 실패', icon: <AlertTriangle className="w-3 h-3" /> }
    };
    return statusMap[settlementStatus] || { color: 'default', text: settlementStatus || 'PENDING', icon: <Clock className="w-3 h-3" /> };
  };

  // 엑셀 다운로드 함수 (헥토/카이로스 정산 탭)
  const handleExportProviderToExcel = async () => {
    try {
      // xlsx 동적 import
      const XLSX = await import('xlsx');
      
      const exportRows = providerPendingData.length > 0 ? providerPendingData : providerFilteredData;
      if (!exportRows || exportRows.length === 0) {
        showToastMessage('다운로드할 데이터가 없습니다.', 'warning');
        return;
      }

      const period = '미정산_전체';

      // 데이터 준비
      const excelData = exportRows.map((record, index) => {
        const completedDate = record.completedAt || record.createdAt;
        const dateStr = completedDate ? dayjs(completedDate.toDate ? completedDate.toDate() : completedDate).format('YYYY-MM-DD') : '-';
        
        const providerSettlement = calculateProviderSettlement(record, financeConfig);

        return {
          '번호': index + 1,
          '송금 ID': record.remitId || '-',
          '송금자 이름': record.userName || '-',
          '송금완료일': dateStr,
          '송금액 (KRW)': record.amountKRW ? `${record.amountKRW.toLocaleString()}원` : '-',
          '수수료 (KRW)': record.feeKRW ? `${record.feeKRW.toLocaleString()}원` : '-',
          '송금업체 정산액 (KRW)': `${providerSettlement.toLocaleString()}원`,
          '정산 상태': record.settlementStatus === 'SETTLED' ? '정산 완료' : '정산 대기'
        };
      });

      // 워크북 생성
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      // 열 너비 설정
      ws['!cols'] = [
        { wch: 8 },   // 번호
        { wch: 25 },  // 송금 ID
        { wch: 15 },  // 송금자 이름
        { wch: 12 },  // 송금완료일
        { wch: 18 },  // 송금액 KRW
        { wch: 18 },  // 수수료 KRW
        { wch: 20 },  // 송금업체 정산액
        { wch: 12 }   // 정산 상태
      ];

      XLSX.utils.book_append_sheet(wb, ws, '송금업체 정산');

      // 파일명 생성
      const fileName = `송금업체_정산_${period}_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`;
      
      // 다운로드
      XLSX.writeFile(wb, fileName);
      showToastMessage('엑셀 파일이 다운로드되었습니다.', 'success');
    } catch (error) {
      console.error('엑셀 다운로드 실패:', error);
      if (error.message?.includes('xlsx')) {
        showToastMessage('xlsx 라이브러리가 설치되지 않았습니다. npm install xlsx를 실행해주세요.', 'error');
      } else {
        showToastMessage('엑셀 다운로드에 실패했습니다: ' + error.message, 'error');
      }
    }
  };

  // 송금업체 계좌 관리 함수들
  const handleCreateAccount = () => {
    setEditingAccount(null);
    setAccountForm({
      bankName: '',
      accountNumber: '',
      accountHolder: '',
      bankCode: '',
      branchCode: '',
      isActive: true,
      description: '',
      dailyLimit: 0,
      monthlyLimit: 0
    });
    setShowAccountModal(true);
  };

  const handleEditAccount = (account) => {
    setEditingAccount(account);
    setAccountForm({
      bankName: account.bankName || '',
      accountNumber: account.accountNumber || '',
      accountHolder: account.accountHolder || '',
      bankCode: account.bankCode || '',
      branchCode: account.branchCode || '',
      isActive: account.isActive !== undefined ? account.isActive : true,
      description: account.description || '',
      dailyLimit: account.dailyLimit || 0,
      monthlyLimit: account.monthlyLimit || 0
    });
    setShowAccountModal(true);
  };

  const handleSaveAccount = async () => {
    try {
      // 필수 필드 검증
      if (!accountForm.bankName || !accountForm.bankName.trim()) {
        showToastMessage('은행명을 입력해주세요.', 'error');
        return;
      }
      if (!accountForm.accountNumber || !accountForm.accountNumber.trim()) {
        showToastMessage('계좌번호를 입력해주세요.', 'error');
        return;
      }
      if (!accountForm.accountHolder || !accountForm.accountHolder.trim()) {
        showToastMessage('예금주를 입력해주세요.', 'error');
        return;
      }
      
      console.log('💾 계좌 저장 시작:', accountForm);
      
      if (editingAccount) {
        // 수정
        await providerAccountService.updateAccount(editingAccount.id, accountForm);
        console.log('✅ 계좌 수정 완료');
        showToastMessage('계좌가 수정되었습니다.', 'success');
      } else {
        // 생성
        await providerAccountService.createAccount(accountForm);
        console.log('✅ 계좌 생성 완료');
        showToastMessage('계좌가 추가되었습니다.', 'success');
      }
      
      // 계좌 목록 새로고침
      await loadProviderAccounts();
      setShowAccountModal(false);
    } catch (error) {
      console.error('❌ 계좌 저장 실패:', error);
      showToastMessage('계좌 저장에 실패했습니다: ' + (error.message || '알 수 없는 오류'), 'error');
    }
  };

  const handleDeleteAccount = async (accountId) => {
    if (window.confirm('이 계좌를 삭제하시겠습니까?')) {
      try {
        console.log('🗑️ 계좌 삭제 시작:', accountId);
        await providerAccountService.deleteAccount(accountId);
        console.log('✅ 계좌 삭제 완료');
        showToastMessage('계좌가 삭제되었습니다.', 'success');
        
        // 계좌 목록 새로고침
        await loadProviderAccounts();
      } catch (error) {
        console.error('❌ 계좌 삭제 실패:', error);
        showToastMessage('계좌 삭제에 실패했습니다: ' + (error.message || '알 수 없는 오류'), 'error');
      }
    }
  };

  const handleToggleAccountStatus = async (accountId) => {
    try {
      console.log('🔄 계좌 상태 변경 시작:', accountId);
      
      const account = providerAccounts.find(acc => acc.id === accountId);
      if (!account) {
        showToastMessage('계좌를 찾을 수 없습니다.', 'error');
        return;
      }
      
      const newStatus = !account.isActive;
      await providerAccountService.toggleAccountStatus(accountId, newStatus);
      console.log('✅ 계좌 상태 변경 완료');
      showToastMessage(`계좌가 ${newStatus ? '활성화' : '비활성화'}되었습니다.`, 'success');
      
      // 계좌 목록 새로고침
      await loadProviderAccounts();
    } catch (error) {
      console.error('❌ 계좌 상태 변경 실패:', error);
      showToastMessage('계좌 상태 변경에 실패했습니다: ' + (error.message || '알 수 없는 오류'), 'error');
    }
  };



  const handleUpdateCache = async () => {
    try {
      setUpdatingCache(true);
      showToastMessage('캐시 업데이트를 시작합니다. 잠시만 기다려주세요... (대규모 데이터일 경우 최대 60분 소요 가능)', 'info');

      const manualUpdate = httpsCallable(functions, 'manualSettlementCacheUpdate');
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('함수 실행 타임아웃 (60분 초과)')), 3600000);
      });
      const result = await Promise.race([manualUpdate(), timeoutPromise]);
      console.log('✅ 배치 작업 완료:', result.data);

      setTimeout(async () => {
        await loadSettlements();
        showToastMessage('캐시 업데이트가 완료되었습니다.', 'success');
      }, 2000);
    } catch (error) {
      console.error('❌ 배치 작업 실패:', error);
      let errorMessage = error.message || '알 수 없는 오류';
      if (errorMessage.includes('timeout') || errorMessage.includes('타임아웃')) {
        errorMessage = '함수 실행이 타임아웃되었습니다. Firebase Console에서 함수 로그를 확인하거나 잠시 후 다시 시도해주세요.';
      } else if (errorMessage.includes('permission') || errorMessage.includes('권한')) {
        errorMessage = '권한이 없습니다. Admin 권한이 있는 계정으로 로그인했는지 확인해주세요.';
      } else if (errorMessage.includes('not found') || errorMessage.includes('존재하지')) {
        errorMessage = 'Cloud Function이 배포되지 않았습니다. functions 폴더에서 배포를 확인해주세요.';
      }
      showToastMessage('캐시 업데이트에 실패했습니다: ' + errorMessage, 'error');
    } finally {
      setUpdatingCache(false);
    }
  };

  const updateFilter = (patch) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setCurrentPage(1);
  };

  const won = (v) => `${Math.round(Number(v) || 0).toLocaleString()}원`;
  const feeRatePct = Number(financeConfig?.feeSettings?.remittanceFeeRatePercent ?? 2.5);
  const hectoPerTx = getHectoFeePerTx(financeConfig);

  const toJsDate = (raw) => {
    if (!raw) return null;
    const d = raw?.toDate ? raw.toDate() : new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const SettlementBadge = ({ status }) => {
    const st = status !== undefined && status !== null ? status : 'PENDING';
    const map = {
      PENDING: { cls: 'bg-amber-50 text-amber-700 ring-amber-200', text: '정산 대기', Icon: Clock },
      SETTLED: { cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200', text: '정산 완료', Icon: CheckCircle },
      FAILED: { cls: 'bg-red-50 text-red-700 ring-red-200', text: '정산 실패', Icon: AlertTriangle },
    };
    const m = map[st] || { cls: 'bg-slate-100 text-slate-600 ring-slate-200', text: st, Icon: Info };
    return (
      <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${m.cls}`}>
        <m.Icon className="h-3 w-3" />
        {m.text}
      </span>
    );
  };

  const SummaryStrip = ({ summary, tone }) => {
    const items = [
      { label: '송금 건수', value: `${summary.count.toLocaleString()}건`, cls: 'text-slate-900', Icon: FileText, iconCls: 'bg-slate-100 text-slate-600' },
      { label: '송금액 (그로스)', value: won(summary.totalAmount), cls: 'text-indigo-700', Icon: Banknote, iconCls: 'bg-indigo-50 text-indigo-600' },
      { label: `Kairos 몫 (수수료 − 헥토)`, value: won(summary.kairosShare), cls: 'text-cyan-700', Icon: TrendingUp, iconCls: 'bg-cyan-50 text-cyan-600' },
      { label: `헥토 몫 (건당 ${hectoPerTx.toLocaleString()}원)`, value: won(summary.hectoShare), cls: 'text-emerald-700', Icon: CreditCard, iconCls: 'bg-emerald-50 text-emerald-600' },
    ];
    return (
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        {items.map(({ label, value, cls, Icon, iconCls }) => (
          <div key={label} className={`flex items-center gap-3 rounded-xl border bg-white px-3 py-2.5 shadow-sm ${tone}`}>
            <span className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${iconCls}`}>
              <Icon className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <div className="truncate text-[11px] text-slate-500">{label}</div>
              <div className={`truncate text-base font-bold tabular-nums leading-tight ${cls}`}>{value}</div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const MonthRow = ({ month, pending }) => {
    const kairosMonth =
      month.kairosSettlement ?? month.remittances.reduce((sum, r) => sum + calculateKairosSettlement(r, financeConfig), 0);
    const gross = month.remittances.reduce((sum, r) => sum + getRemittanceGrossKRW(r), 0);
    const monthLabel = month.monthKey || dayjs(month.startDate).format('YYYY-MM');
    const isSettling = settlingMonthKey === monthLabel && batchSettling;
    const openDetails = () => {
      setSelectedWeek(month);
      setShowWeekDetails(true);
    };
    return (
      <div className="flex flex-wrap items-center gap-3 px-3 py-2.5 hover:bg-slate-50">
        <button type="button" onClick={openDetails} className="flex min-w-[150px] !min-h-0 items-center gap-2.5 !p-0 text-left">
          <span className={`flex h-9 w-9 flex-shrink-0 flex-col items-center justify-center rounded-lg leading-none ${pending ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`}>
            <span className="text-[9px] font-semibold">{dayjs(month.startDate).format('YYYY')}</span>
            <span className="text-sm font-bold">{dayjs(month.startDate).format('M')}월</span>
          </span>
          <span>
            <span className="block text-[13px] font-semibold text-slate-900">{dayjs(month.startDate).format('YYYY년 M월')}</span>
            <span className="block text-[11px] text-slate-500">
              {dayjs(month.startDate).format('MM/DD')} ~ {dayjs(month.endDate).format('MM/DD')} · {month.transactionCount}건
            </span>
          </span>
        </button>

        <div className="grid flex-1 grid-cols-3 gap-3 text-right sm:min-w-[420px]">
          <div>
            <div className="text-[10px] text-slate-500">송금액</div>
            <div className="text-[13px] font-bold tabular-nums text-indigo-700">{won(gross)}</div>
          </div>
          <div>
            <div className="text-[10px] text-slate-500">Kairos 몫</div>
            <div className="text-[13px] font-bold tabular-nums text-cyan-700">{won(kairosMonth)}</div>
          </div>
          <div>
            <div className="text-[10px] text-slate-500">헥토 몫</div>
            <div className="text-[13px] font-bold tabular-nums text-emerald-700">{won(month.providerSettlement)}</div>
          </div>
        </div>

        <div className="ml-auto flex items-center gap-1.5">
          <button
            type="button"
            onClick={openDetails}
            className="inline-flex !h-8 !min-h-0 items-center gap-1 rounded-md border border-slate-200 bg-white !px-2.5 !py-0 text-xs font-semibold text-slate-600 hover:bg-slate-50"
          >
            <Eye className="h-3.5 w-3.5" />
            상세
          </button>
          {pending ? (
            <button
              type="button"
              disabled={batchSettling}
              onClick={() => handleMonthlySettlement(month)}
              className="inline-flex !h-8 !min-h-0 items-center gap-1 rounded-md bg-emerald-600 !px-3 !py-0 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {isSettling ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle className="h-3.5 w-3.5" />}
              {dayjs(month.startDate).format('M월')} 정산 완료
            </button>
          ) : (
            <SettlementBadge status="SETTLED" />
          )}
        </div>
      </div>
    );
  };

  const EmptyState = ({ Icon, text }) => (
    <div className="flex flex-col items-center justify-center py-10 text-slate-500">
      <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-slate-100">
        <Icon className="h-5 w-5 text-slate-400" />
      </div>
      <p className="text-sm">{text}</p>
    </div>
  );

  const activeAccounts = providerAccounts.filter((acc) => acc.isActive).length;

  const TABS = [
    { key: 'provider-settlement', label: '정산 전', Icon: Clock, count: providerPendingSummary.count },
    { key: 'provider-settled', label: '정산 완료', Icon: CheckCircle, count: providerSettledSummary.count },
    { key: 'settlements', label: '전체 거래 목록', Icon: Calculator, count: settlements.length },
    { key: 'provider-accounts', label: '계좌 관리', Icon: Banknote, count: activeAccounts },
  ];

  const transactionColumns = [
    {
      title: '거래 ID',
      dataIndex: 'remitId',
      key: 'remitId',
      width: 190,
      render: (text) => <span className="font-mono text-[11px] text-slate-700">{text || '-'}</span>,
    },
    {
      title: '사용자',
      dataIndex: 'userName',
      key: 'userName',
      width: 140,
      render: (name, r) => (
        <div className="min-w-0">
          <div className="truncate text-[13px] text-slate-800">{name || '-'}</div>
          {r.userId && <div className="truncate text-[10px] text-slate-400">{r.userId}</div>}
        </div>
      ),
    },
    {
      title: '완료일',
      key: 'completed',
      width: 110,
      sorter: (a, b) => (toJsDate(a.uabCompletedAt || a.completedAt || a.createdAt)?.getTime() || 0) - (toJsDate(b.uabCompletedAt || b.completedAt || b.createdAt)?.getTime() || 0),
      render: (_, r) => {
        const d = toJsDate(r.uabCompletedAt || r.completedAt || r.createdAt);
        return d ? (
          <div className="text-[12px] leading-tight">
            <div className="text-slate-700">{dayjs(d).format('YYYY-MM-DD')}</div>
            <div className="text-slate-400">{dayjs(d).format('HH:mm')}</div>
          </div>
        ) : '-';
      },
    },
    {
      title: '송금액',
      key: 'amount',
      align: 'right',
      width: 140,
      sorter: (a, b) => (a.amountKRW || 0) - (b.amountKRW || 0),
      render: (_, r) => (
        <div className="leading-tight">
          <div className="text-[13px] font-semibold tabular-nums text-slate-900">{won(r.amountKRW)}</div>
          <div className="text-[10px] tabular-nums text-slate-400">{(r.amountMMK || 0).toLocaleString()} MMK</div>
        </div>
      ),
    },
    {
      title: '수수료',
      dataIndex: 'feeKRW',
      key: 'feeKRW',
      align: 'right',
      width: 100,
      render: (fee) => <span className="text-[12px] tabular-nums text-orange-700">{won(fee)}</span>,
    },
    {
      title: 'Kairos 몫',
      key: 'kairos',
      align: 'right',
      width: 100,
      render: (_, r) => <span className="text-[12px] tabular-nums text-cyan-700">{won(calculateKairosSettlement(r, financeConfig))}</span>,
    },
    {
      title: '헥토 몫',
      key: 'hecto',
      align: 'right',
      width: 100,
      render: (_, r) => <span className="text-[12px] tabular-nums text-emerald-700">{won(calculateProviderSettlement(r, financeConfig))}</span>,
    },
    {
      title: '정산 상태',
      dataIndex: 'settlementStatus',
      key: 'settlementStatus',
      width: 100,
      render: (status) => <SettlementBadge status={status} />,
    },
  ];

  const inputCls =
    'h-9 w-full rounded-lg border border-slate-200 px-3 text-[13px] focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200';

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <RefreshCw className="h-4 w-4 animate-spin" />
          정산 데이터를 불러오는 중...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-3">
      <div className="space-y-3">
        {/* Header */}
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-600">
                <Calculator className="h-4 w-4 text-white" />
              </div>
              <div>
                <h1 className="text-[15px] font-semibold leading-tight text-slate-900">통합 정산 관리</h1>
                <p className="text-[11px] leading-tight text-slate-500">
                  송금 완료(COMPLETED) 거래를 월 단위로 정산 · 수수료 {feeRatePct}% = 헥토 건당 {hectoPerTx.toLocaleString()}원 + Kairos 나머지
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50/50 px-2.5 py-1.5">
                <Clock className="h-3.5 w-3.5 text-amber-600" />
                <span>
                  <span className="block text-[10px] leading-tight text-slate-500">정산 대기</span>
                  <span className="block text-sm font-bold leading-tight tabular-nums text-slate-900">
                    {providerPendingSummary.count.toLocaleString()}건 · {won(providerPendingSummary.totalAmount)}
                  </span>
                </span>
              </div>
              <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/50 px-2.5 py-1.5">
                <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />
                <span>
                  <span className="block text-[10px] leading-tight text-slate-500">정산 완료</span>
                  <span className="block text-sm font-bold leading-tight tabular-nums text-slate-900">
                    {providerSettledSummary.count.toLocaleString()}건 · {won(providerSettledSummary.totalAmount)}
                  </span>
                </span>
              </div>
              <Tooltip title="송금·금융설정·계좌 데이터를 다시 불러옵니다">
                <button
                  type="button"
                  onClick={() => {
                    loadSettlements();
                    loadFinanceConfig();
                    loadProviderAccounts();
                  }}
                  className="inline-flex !h-8 !min-h-0 items-center gap-1.5 rounded-md border border-slate-200 bg-white !px-3 !py-0 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  새로고침
                </button>
              </Tooltip>
              <Tooltip title="Cloud Function으로 정산 캐시를 재계산합니다 (대량 데이터 시 오래 걸릴 수 있음)">
                <button
                  type="button"
                  onClick={handleUpdateCache}
                  disabled={updatingCache}
                  className="inline-flex !h-8 !min-h-0 items-center gap-1.5 rounded-md bg-slate-900 !px-3 !py-0 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
                >
                  <Zap className={`h-3.5 w-3.5 ${updatingCache ? 'animate-pulse' : ''}`} />
                  {updatingCache ? '업데이트 중...' : 'Update Cache'}
                </button>
              </Tooltip>
            </div>
          </div>

          {/* Tab bar */}
          <div className="-mx-1 mt-3 flex gap-1 overflow-x-auto border-t border-slate-100 px-1 pt-2.5">
            {TABS.map(({ key, label, Icon, count }) => {
              const active = activeTab === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActiveTab(key)}
                  className={`inline-flex !h-8 !min-h-0 flex-shrink-0 items-center gap-1.5 rounded-lg !px-3 !py-0 text-xs font-semibold transition-colors ${
                    active ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {label}
                  <span className={`rounded-full px-1.5 text-[10px] tabular-nums ${active ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'}`}>
                    {count.toLocaleString()}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* 정산 전 탭 */}
        {activeTab === 'provider-settlement' && (
          <>
            <SummaryStrip summary={providerPendingSummary} tone="border-slate-200" />
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
                <div>
                  <div className="text-[13px] font-semibold text-slate-800">월별 정산 대기</div>
                  <div className="text-[11px] text-slate-500">미정산 건을 월 단위로 일괄 정산합니다 (건별 정산 없음)</div>
                </div>
                <button
                  type="button"
                  onClick={handleExportProviderToExcel}
                  className="inline-flex !h-8 !min-h-0 items-center gap-1.5 rounded-md border border-slate-200 bg-white !px-3 !py-0 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                >
                  <Download className="h-3.5 w-3.5" />
                  Excel 다운로드
                </button>
              </div>
              {pendingMonthlySettlements.length === 0 ? (
                <EmptyState Icon={Banknote} text="정산 전 거래가 없습니다." />
              ) : (
                <div className="divide-y divide-slate-100">
                  {pendingMonthlySettlements.map((month) => (
                    <MonthRow key={month.monthKey || month.weekKey} month={month} pending />
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* 정산 완료 탭 */}
        {activeTab === 'provider-settled' && (
          <>
            <SummaryStrip summary={providerSettledSummary} tone="border-slate-200" />
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 bg-slate-50 px-3 py-2">
                <div className="text-[13px] font-semibold text-slate-800">월별 정산 완료 내역</div>
                <div className="text-[11px] text-slate-500">월을 누르면 거래 상세를 볼 수 있습니다</div>
              </div>
              {settledMonthlySettlements.length === 0 ? (
                <EmptyState Icon={CheckCircle} text="정산 완료 내역이 없습니다." />
              ) : (
                <div className="divide-y divide-slate-100">
                  {settledMonthlySettlements.map((month) => (
                    <MonthRow key={month.monthKey || month.weekKey} month={month} pending={false} />
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* 전체 거래 목록 탭 */}
        {activeTab === 'settlements' && (
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="grid grid-cols-2 gap-px border-b border-slate-200 bg-slate-200 md:grid-cols-5">
              {[
                ['조회 건수', `${stats.total.toLocaleString()}건`, 'text-slate-900'],
                ['정산 대기', `${stats.pending.toLocaleString()}건`, 'text-amber-700'],
                ['정산 완료', `${stats.settled.toLocaleString()}건`, 'text-emerald-700'],
                ['총 송금액', won(stats.totalAmount), 'text-indigo-700'],
                ['총 수수료', won(stats.totalFees), 'text-orange-700'],
              ].map(([label, value, cls]) => (
                <div key={label} className="bg-white px-3 py-2">
                  <div className="text-[10px] text-slate-500">{label}</div>
                  <div className={`text-sm font-bold tabular-nums ${cls}`}>{value}</div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
              <Input
                allowClear
                size="small"
                prefix={<Search className="h-3.5 w-3.5 text-slate-400" />}
                placeholder="거래 ID, 사용자명, 사용자 ID 검색..."
                value={filters.searchTerm}
                onChange={(e) => updateFilter({ searchTerm: e.target.value })}
                className="!h-8 !w-72"
              />
              <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
                {[
                  ['all', '전체'],
                  ['PENDING', '대기'],
                  ['SETTLED', '완료'],
                  ['FAILED', '실패'],
                ].map(([v, l]) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => updateFilter({ status: v })}
                    className={`!h-7 !min-h-0 rounded-md !px-2.5 !py-0 text-[11px] font-semibold ${
                      filters.status === v ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
              <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
                {[
                  ['7days', '7일'],
                  ['30days', '30일'],
                  ['90days', '90일'],
                  ['all', '전체 기간'],
                ].map(([v, l]) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => updateFilter({ dateRange: v })}
                    className={`!h-7 !min-h-0 rounded-md !px-2.5 !py-0 text-[11px] font-semibold ${
                      filters.dateRange === v ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
              <Select
                size="small"
                value={filters.amountRange}
                onChange={(value) => updateFilter({ amountRange: value })}
                className="w-36"
                options={[
                  { value: 'all', label: '금액: 전체' },
                  { value: '0-100000', label: '10만원 미만' },
                  { value: '100000-500000', label: '10만 ~ 50만원' },
                  { value: '500000+', label: '50만원 이상' },
                ]}
              />
              <span className="ml-auto text-[11px] text-slate-500">
                {filteredSettlements.length.toLocaleString()} / {settlements.length.toLocaleString()}건
              </span>
            </div>

            <Table
              columns={transactionColumns}
              dataSource={filteredSettlements}
              rowKey={(r) => r.id || r.remitId}
              size="small"
              scroll={{ x: 980 }}
              locale={{ emptyText: <EmptyState Icon={Search} text="조건에 맞는 거래가 없습니다." /> }}
              pagination={{
                current: currentPage,
                pageSize,
                total: filteredSettlements.length,
                onChange: (page, size) => {
                  setCurrentPage(page);
                  if (size !== pageSize) setPageSize(size);
                },
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                size: 'small',
                showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}건`,
              }}
            />
          </div>
        )}

        {/* 송금업체 계좌 관리 탭 */}
        {activeTab === 'provider-accounts' && (
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="grid grid-cols-2 gap-px border-b border-slate-200 bg-slate-200 md:grid-cols-4">
              {[
                ['총 계좌', `${providerAccounts.length}개`, 'text-slate-900'],
                ['활성 계좌', `${activeAccounts}개`, 'text-emerald-700'],
                ['총 사용 건수', `${providerAccounts.reduce((sum, acc) => sum + (acc.usageCount || 0), 0).toLocaleString()}건`, 'text-violet-700'],
                ['총 처리 금액', won(providerAccounts.reduce((sum, acc) => sum + (acc.totalAmount || 0), 0)), 'text-orange-700'],
              ].map(([label, value, cls]) => (
                <div key={label} className="bg-white px-3 py-2">
                  <div className="text-[10px] text-slate-500">{label}</div>
                  <div className={`text-sm font-bold tabular-nums ${cls}`}>{value}</div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
              <div>
                <div className="text-[13px] font-semibold text-slate-800">송금업체 계좌</div>
                <div className="text-[11px] text-slate-500">사용자가 송금 시 선택할 수 있는 입금 계좌입니다</div>
              </div>
              <button
                type="button"
                onClick={handleCreateAccount}
                className="inline-flex !h-8 !min-h-0 items-center gap-1.5 rounded-md bg-slate-900 !px-3 !py-0 text-xs font-semibold text-white hover:bg-slate-800"
              >
                <Plus className="h-3.5 w-3.5" />
                계좌 추가
              </button>
            </div>

            {providerLoading ? (
              <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-500">
                <RefreshCw className="h-4 w-4 animate-spin" />
                계좌 정보를 불러오는 중...
              </div>
            ) : providerAccounts.length === 0 ? (
              <EmptyState Icon={Banknote} text="등록된 계좌가 없습니다." />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[860px] text-left">
                  <thead className="border-b border-slate-200">
                    <tr className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      <th className="px-3 py-2">은행 / 예금주</th>
                      <th className="px-3 py-2">계좌번호</th>
                      <th className="px-3 py-2 text-right">한도 (일 / 월)</th>
                      <th className="px-3 py-2 text-right">사용 현황</th>
                      <th className="px-3 py-2">상태</th>
                      <th className="px-3 py-2 text-right" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {providerAccounts.map((account) => (
                      <tr key={account.id} className="text-[13px] hover:bg-slate-50">
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2.5">
                            <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
                              <Banknote className="h-4 w-4" />
                            </span>
                            <div className="min-w-0">
                              <div className="font-semibold text-slate-900">{account.bankName}</div>
                              <div className="truncate text-[11px] text-slate-500">
                                {account.accountHolder}
                                {account.description ? ` · ${account.description}` : ''}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="font-mono text-[13px] text-slate-900">{account.accountNumber}</div>
                          {(account.bankCode || account.branchCode) && (
                            <div className="text-[10px] text-slate-400">
                              {account.bankCode || '-'} / {account.branchCode || '-'}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right text-[12px] tabular-nums text-slate-700">
                          <div>{won(account.dailyLimit)}</div>
                          <div className="text-slate-400">{won(account.monthlyLimit)}</div>
                        </td>
                        <td className="px-3 py-2 text-right text-[12px] tabular-nums text-slate-700">
                          <div>{(account.usageCount || 0).toLocaleString()}건</div>
                          <div className="text-slate-400">{won(account.totalAmount)}</div>
                        </td>
                        <td className="px-3 py-2">
                          <Switch
                            size="small"
                            checked={!!account.isActive}
                            onChange={() => handleToggleAccountStatus(account.id)}
                          />
                          <span className={`ml-2 text-[11px] font-semibold ${account.isActive ? 'text-emerald-700' : 'text-slate-400'}`}>
                            {account.isActive ? '활성' : '비활성'}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center justify-end gap-0.5">
                            <Tooltip title="수정">
                              <button
                                type="button"
                                onClick={() => handleEditAccount(account)}
                                className="flex !h-7 !min-h-0 !w-7 !min-w-0 items-center justify-center rounded-md !p-0 text-slate-400 hover:bg-indigo-50 hover:text-indigo-600"
                              >
                                <Edit className="h-3.5 w-3.5" />
                              </button>
                            </Tooltip>
                            <Tooltip title="삭제">
                              <button
                                type="button"
                                onClick={() => handleDeleteAccount(account.id)}
                                className="flex !h-7 !min-h-0 !w-7 !min-w-0 items-center justify-center rounded-md !p-0 text-slate-400 hover:bg-red-50 hover:text-red-600"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </Tooltip>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 월별 정산 상세 모달 */}
      {showWeekDetails && selectedWeek && (
        <Modal
          title={
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-slate-500" />
              <span className="text-[15px] font-semibold">{dayjs(selectedWeek.startDate).format('YYYY년 M월')} 정산 상세</span>
              <span className="text-[12px] font-normal text-slate-500">
                {dayjs(selectedWeek.startDate).format('YYYY-MM-DD')} ~ {dayjs(selectedWeek.endDate).format('YYYY-MM-DD')}
              </span>
            </div>
          }
          open={showWeekDetails}
          onCancel={() => {
            setShowWeekDetails(false);
            setSelectedWeek(null);
          }}
          footer={null}
          width={1100}
        >
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200 md:grid-cols-5">
              {[
                ['거래 건수', `${selectedWeek.transactionCount}건`, 'text-slate-900'],
                ['총 송금액', won(selectedWeek.totalAmount), 'text-indigo-700'],
                ['총 수수료', won(selectedWeek.totalFees), 'text-orange-700'],
                [
                  'Kairos (수수료 − 헥토)',
                  won(
                    selectedWeek.kairosSettlement ??
                      selectedWeek.remittances.reduce((sum, r) => sum + calculateKairosSettlement(r, financeConfig), 0)
                  ),
                  'text-cyan-700',
                ],
                [`헥토 (건당 ${hectoPerTx.toLocaleString()}원)`, won(selectedWeek.providerSettlement), 'text-emerald-700'],
              ].map(([label, value, cls]) => (
                <div key={label} className="bg-white px-3 py-2">
                  <div className="text-[10px] text-slate-500">{label}</div>
                  <div className={`text-sm font-bold tabular-nums ${cls}`}>{value}</div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between">
              <div className="text-[12px] text-slate-500">건별 정산 없이 월 단위로만 처리합니다</div>
              {activeTab === 'provider-settlement' && (
                <Button
                  type="primary"
                  size="small"
                  loading={batchSettling}
                  onClick={() => handleMonthlySettlement(selectedWeek)}
                  disabled={
                    batchSettling ||
                    selectedWeek.remittances.filter((r) => (r.settlementStatus ?? 'PENDING') === 'PENDING').length === 0
                  }
                >
                  {dayjs(selectedWeek.startDate).format('M월')} 정산 완료
                </Button>
              )}
            </div>

            <Table
              dataSource={selectedWeek.remittances}
              columns={transactionColumns}
              rowKey={(r) => r.id || r.remitId}
              pagination={{ pageSize: 10, size: 'small' }}
              size="small"
              scroll={{ x: 980, y: 420 }}
            />
          </div>
        </Modal>
      )}

      {/* 정산 처리 안내 모달 */}
      <Modal
        open={showSettlementModal && !!selectedSettlement}
        onCancel={() => setShowSettlementModal(false)}
        title="정산 처리"
        width={420}
        footer={[
          <Button key="close" onClick={() => setShowSettlementModal(false)}>
            닫기
          </Button>,
          <Button
            key="go"
            type="primary"
            onClick={() => {
              setShowSettlementModal(false);
              setActiveTab('provider-settlement');
            }}
          >
            월별 정산으로 이동
          </Button>,
        ]}
      >
        {selectedSettlement && (
          <div className="space-y-2 text-[13px]">
            <div className="grid grid-cols-3 gap-y-1 rounded-lg bg-slate-50 p-3">
              <span className="text-slate-500">거래 ID</span>
              <span className="col-span-2 font-mono text-[12px]">{selectedSettlement.remitId}</span>
              <span className="text-slate-500">사용자</span>
              <span className="col-span-2">{selectedSettlement.userName}</span>
              <span className="text-slate-500">송금액</span>
              <span className="col-span-2">{won(selectedSettlement.amountKRW)}</span>
              <span className="text-slate-500">수수료</span>
              <span className="col-span-2">{won(selectedSettlement.feeKRW)}</span>
            </div>
            <p className="text-amber-700">
              정산은 건별이 아니라 <strong>월 단위</strong>로만 처리합니다. 「정산 전」 탭에서 해당 월의 「정산 완료」를 눌러주세요.
            </p>
          </div>
        )}
      </Modal>

      {/* 계좌 추가/수정 모달 */}
      <Modal
        open={showAccountModal}
        onCancel={() => setShowAccountModal(false)}
        title={editingAccount ? '계좌 수정' : '계좌 추가'}
        width={620}
        footer={[
          <Button key="cancel" onClick={() => setShowAccountModal(false)}>
            취소
          </Button>,
          <Button key="save" type="primary" onClick={handleSaveAccount}>
            {editingAccount ? '수정' : '추가'}
          </Button>,
        ]}
      >
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {[
            ['bankName', '은행명 *', '예: 신한은행', 'text'],
            ['accountNumber', '계좌번호 *', '예: 110-123-456789', 'text'],
            ['accountHolder', '예금주 *', '예: 카이로스 송금', 'text'],
            ['bankCode', '은행코드', '예: 088', 'text'],
            ['branchCode', '지점코드', '예: 001', 'text'],
            ['dailyLimit', '일일 한도 (원)', '10000000', 'number'],
            ['monthlyLimit', '월간 한도 (원)', '300000000', 'number'],
          ].map(([field, label, placeholder, type]) => (
            <div key={field}>
              <label className="mb-1 block text-xs font-semibold text-slate-700">{label}</label>
              <input
                type={type}
                value={accountForm[field]}
                onChange={(e) =>
                  setAccountForm({
                    ...accountForm,
                    [field]: type === 'number' ? parseInt(e.target.value, 10) || 0 : e.target.value,
                  })
                }
                className={inputCls}
                placeholder={placeholder}
              />
            </div>
          ))}
          <div className="md:col-span-2">
            <label className="mb-1 block text-xs font-semibold text-slate-700">설명</label>
            <input
              type="text"
              value={accountForm.description}
              onChange={(e) => setAccountForm({ ...accountForm, description: e.target.value })}
              className={inputCls}
              placeholder="예: 메인 송금 계좌"
            />
          </div>
          <label className="flex items-center gap-2 md:col-span-2">
            <Switch
              size="small"
              checked={accountForm.isActive}
              onChange={(checked) => setAccountForm({ ...accountForm, isActive: checked })}
            />
            <span className="text-[13px] font-medium text-slate-700">활성 상태</span>
          </label>
        </div>
      </Modal>
    </div>
  );
};

export default SettlementManagementPage;